import { RuleTargetAction } from '@ee/common/enums';
import { LayoutRule, LayoutRuleCondition, RequirednessGate, RuleScope } from '@ee/common/models';

/**
 * What the rules say about the form right now.
 *
 * Evaluated on the client, in the wizard, on every value change. The server evaluates the same rules
 * once at submission and is the authority — but a client cannot wait for a round trip to find out
 * whether the question in front of them still applies, and a form that reveals a field a second after
 * you answer the one above it reads as broken.
 *
 * Kept in step with `LayoutRuleEvaluator.kt` deliberately, including the parts that look fussy:
 * numbers compare as numbers and dates as dates, because stringifying is what made *is greater than*
 * on a currency field compare "1000" against "900" lexically.
 */
export interface RuleOutcome {
  /** Storage keys and section keys the rules hide. */
  hidden: ReadonlySet<string>;
  /** Storage keys the rules require, beyond whatever the placement already requires. */
  required: ReadonlySet<string>;
  /**
   * Why a field is being asked for, by storage key.
   *
   * Authored on the rule, not derived — a firm writes "Asked because no rent amount is entered" and it
   * is shown in place. A client meeting a question that was not there a moment ago needs the reason
   * more than the person who wrote the rule does.
   */
  reasons: ReadonlyMap<string, string>;
  /**
   * Shown but read-only. Not hidden: the control keeps its value and the server keeps it too — DISABLE
   * was once folded into hide, which on the server meant deleting what was typed (EE-412 review).
   */
  disabled: ReadonlySet<string>;
  /** Storage keys a rule makes optional; they are never required to submit. */
  relaxed: ReadonlySet<string>;
}

export const NO_RULES: RuleOutcome = {
  hidden: new Set(),
  required: new Set(),
  reasons: new Map(),
  disabled: new Set(),
  relaxed: new Set()
};

function appliesTo(scope: RuleScope, surface: 'INTAKE' | 'CASE'): boolean {
  if (scope === 'EVERYWHERE') {
    return true;
  }
  return scope === surface;
}

/**
 * A value as text, for the string operators.
 *
 * Only scalars have one. An object answer compared with `contains` would otherwise match on
 * "[object Object]", which is a rule that fires for reasons nobody authored.
 */
function text(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  // Trimmed, as the Kotlin evaluator reads it: "Section 8 " typed with a trailing space is still Section 8.
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return value.toString();
  }
  // A multi-value answer reads as its values joined — the same text the Kotlin evaluator compares.
  if (Array.isArray(value)) {
    return value.map((one) => text(one).trim()).join(', ');
  }
  return '';
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

/** `$1,200.00` is a number. Currency is entered the way people write it, not the way it compares. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const cleaned = value.trim().replace(/[$,]/g, '');
  if (!cleaned.length) {
    return null;
  }
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * An ISO calendar date, and nothing else — what Kotlin's `LocalDate.parse` accepts.
 *
 * Not `Date.parse`: V8 reads "Section 8" and "Unit 12" as dates, so text would order as dates here and
 * not on the server.
 */
function asDate(value: unknown): number | null {
  if (typeof value !== 'string') {
    return null;
  }
  // An exact ISO date, or a full ISO date-time read as its date — never the first ten characters of any
  // text ("2026-01-01 garbage"). The same pattern as the Kotlin evaluator.
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})?)?$/.test(trimmed)) {
    return null;
  }
  const day = trimmed.slice(0, 10);
  const parsed = Date.parse(`${day}T00:00:00Z`);
  return Number.isNaN(parsed) || new Date(parsed).toISOString().slice(0, 10) !== day ? null : parsed;
}

/** Negative, zero or positive, or null when the two are not comparable as an ordered pair. */
function compare(actual: unknown, expected: unknown): number | null {
  const leftNumber = asNumber(actual);
  const rightNumber = asNumber(expected);
  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }
  const leftDate = asDate(actual);
  const rightDate = asDate(expected);
  if (leftDate !== null && rightDate !== null) {
    return leftDate - rightDate;
  }
  if (typeof actual === 'boolean' || typeof expected === 'boolean') {
    return text(actual) === text(expected) ? 0 : null;
  }
  return null;
}

function equals(actual: unknown, expected: unknown): boolean {
  // A missing side equals only another missing side, as in Kotlin: "is (no value)" is not "is empty".
  if (actual === null || actual === undefined || expected === null || expected === undefined) {
    return (actual ?? null) === (expected ?? null);
  }
  const numeric = compare(actual, expected);
  if (numeric !== null) {
    return numeric === 0;
  }
  return text(actual).toLowerCase() === text(expected).toLowerCase();
}

function matchesCondition(condition: LayoutRuleCondition, values: Record<string, unknown>): boolean {
  const actual = condition.field_key ? values[condition.field_key] : undefined;
  const expected = condition.value;
  const ordered = (test: (n: number) => boolean): boolean => {
    const result = compare(actual, expected);
    return result === null ? false : test(result);
  };

  switch (condition.operator) {
    case 'blank':
      return isBlank(actual);
    case 'none':
      return !isBlank(actual);
    case '==':
      return equals(actual, expected);
    case 'in':
      return (Array.isArray(expected) ? expected : [expected]).some((one) => equals(actual, one));
    case 'contains':
      return text(actual).toLowerCase().includes(text(expected).toLowerCase());
    case 'not_contains':
      return !text(actual).toLowerCase().includes(text(expected).toLowerCase());
    case 'starts_with':
      return text(actual).toLowerCase().startsWith(text(expected).toLowerCase());
    case 'ends_with':
      return text(actual).toLowerCase().endsWith(text(expected).toLowerCase());
    case '>':
      return ordered((n) => n > 0);
    case '<':
      return ordered((n) => n < 0);
    case '>=':
      return ordered((n) => n >= 0);
    case '<=':
      return ordered((n) => n <= 0);
    default:
      return false;
  }
}

/** A rule with no conditions never fires. An empty condition list is an unfinished rule, not "always". */
export function ruleMatches(rule: LayoutRule, values: Record<string, unknown>): boolean {
  if (!rule.conditions?.length) {
    return false;
  }
  return rule.conjunction === 'ANY'
    ? rule.conditions.some((condition) => matchesCondition(condition, values))
    : rule.conditions.every((condition) => matchesCondition(condition, values));
}

/**
 * Apply every active rule to the current answers.
 *
 * SHOW hides its targets until one of its rules matches, then removes them from HIDE too, because an
 * explicit reveal is the later, more specific intent — the same resolution order the server uses. That
 * beats a HIDE on the SAME target only: a hidden section still hides every field in it, so "hide the
 * section except this field" is not supported (on either side).
 */
export function evaluateRules(
  rules: LayoutRule[],
  values: Record<string, unknown>,
  surface: 'INTAKE' | 'CASE' = 'INTAKE',
  /** Which requiredness gate REQUIRE and OPTIONAL feed: the wizard's submit gate, or a case's workflow gate. */
  gate: RequirednessGate = 'SUBMIT'
): RuleOutcome {
  const hidden = new Set<string>();
  const required = new Set<string>();
  const reasons = new Map<string, string>();
  const disabled = new Set<string>();
  const relaxed = new Set<string>();

  const active = (rules ?? []).filter((rule) => rule.active && appliesTo(rule.scope, surface));

  active
    .filter((rule) => rule.action !== RuleTargetAction.SHOW)
    .filter((rule) => ruleMatches(rule, values))
    .forEach((rule) => {
      rule.targets.forEach((target) => {
        if (rule.action === RuleTargetAction.HIDE) {
          hidden.add(target.ref);
          return;
        }
        if (rule.action === RuleTargetAction.DISABLE) {
          disabled.add(target.ref);
          return;
        }
        if (rule.action === RuleTargetAction.OPTIONAL) {
          // As with REQUIRE, only the gate being evaluated counts: relaxing "needed to file" must not
          // relax what the client has to answer.
          if ((rule.requiredness_gate ?? 'SUBMIT') === gate) {
            relaxed.add(target.ref);
          }
          return;
        }
        // Only the gate being evaluated counts. The wizard asks for the submit gate: the workflow gate is
        // a fact about a case, and enforcing it there would block a client on something their attorney
        // has not started. The case panel asks for the workflow gate ("Needed to file").
        if ((rule.requiredness_gate ?? 'SUBMIT') === gate) {
          required.add(target.ref);
        }
        if (rule.reason) {
          reasons.set(target.ref, rule.reason);
        }
      });
    });

  // SHOW is conditional presence: a target is hidden unless one of its SHOW rules matches, which is
  // what "Show X when Y" reads as. The same order as the server.
  const showRules = active.filter((rule) => rule.action === RuleTargetAction.SHOW);
  const shown = new Set<string>();
  showRules.forEach((rule) => {
    const matched = ruleMatches(rule, values);
    rule.targets.forEach((target) => {
      hidden.add(target.ref);
      if (matched) {
        shown.add(target.ref);
        if (rule.reason) {
          reasons.set(target.ref, rule.reason);
        }
      }
    });
  });
  shown.forEach((ref) => hidden.delete(ref));

  return { hidden, required, reasons, disabled, relaxed };
}
